import { IRef } from '@/models/IFunctionality';
import { IProperty } from '@/models/IProperty';
import { IArray, IObject, IRecord, IRecords } from '@/models/IRecord';
import { generateRandomValue } from '@/utils/functionality-utils';
import { Card } from '@destination/components';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';
import { ModelDocumentation } from '../../ModelDocumentation';

export interface IFunctionalityRecordDetail {
  record: IRecord;
  records: IRecords;
  hideDocs?: boolean;
  name?: string;
}

function isIRef(object: IRef | IRecord): object is IRef {
  return '$ref' in object;
}
export default function FunctionalityRecordDetail({
  record,
  records,
  hideDocs,
  name
}: IFunctionalityRecordDetail) {
  const { t } = useTranslation(undefined, { keyPrefix: 'model' });

  const parseRecordValue = (record: IRecord) => {
    let result;
    if (record.enum) {
      result = `[${record.enum.map(value => `'${value}'`).join(', ')}]`;
    } else if (record.format) {
      result = `${record.format}`;
    } else {
      result = generateRandomValue(record.type);
    }
    if (record.unit) {
      result += ` (${record.unit})`;
    }
    return result;
  };

  const parseRecordObject = (object: IObject, parsedRecords = [name]) => {
    const jsonObject: { [key: string]: unknown } = {};
    const properties = object.properties as IProperty;
    const required = object.required;

    if (properties) {
      Object.entries(properties).forEach(([key, property]) => {
        const { $ref, type, unit } = property as IProperty;

        let jsonKey = key;
        if (!required.includes(key)) {
          jsonKey += '?';
        }

        if ($ref) {
          const refPropertyKey = ($ref as string).split('/').pop() as string;
          if (parsedRecords.includes(refPropertyKey)) {
            jsonObject[jsonKey] = { $ref };
            return;
          }
          const refObject = records[refPropertyKey] as IRecord;
          switch (refObject.type) {
            case 'object':
              jsonObject[jsonKey] = parseRecordObject(
                refObject as IObject,
                parsedRecords.concat(refPropertyKey)
              );
              break;
            case 'array':
              jsonObject[jsonKey] = parseRecordArray(refObject as IArray);
              break;
            default:
              jsonObject[jsonKey] = parseRecordValue(refObject);
          }
        } else {
          let value = generateRandomValue(type);
          if (unit) {
            value += ` (${unit})`;
          }
          jsonObject[jsonKey] = value;
        }
      });
    }

    return jsonObject;
  };

  const parseRecordArray = (array: IArray) => {
    let items;
    const rawItems = array.items;
    if (isIRef(rawItems)) {
      const refPropertyKey = rawItems.$ref.split('/').pop() as string;
      items = records[refPropertyKey] as IRecord;
    } else {
      items = array.items as IRecord;
    }
    const jsonArray: unknown[] = [];

    jsonArray.push(
      items.type === 'object'
        ? parseRecordObject(items as IObject)
        : parseRecordValue(items)
    );

    return jsonArray;
  };

  let content: ReactNode;
  let testId: string;

  switch (record.type) {
    case 'object':
      testId = 'functionality-record-detail-object';
      content = (
        <>
          <ReactJson
            name={null}
            quotesOnKeys={false}
            displayDataTypes={true}
            displayObjectSize={false}
            src={parseRecordObject(record as IObject)}
          />
          <div className="mt-3 text-sm">{t('optional_disclaimer')}</div>
        </>
      );
      break;
    case 'array':
      testId = 'functionality-record-detail-array';
      content = (
        <>
          <ReactJson
            name={null}
            quotesOnKeys={false}
            displayDataTypes={true}
            displayObjectSize={false}
            src={parseRecordArray(record as IArray)}
          />
          <div className="mt-3 text-sm">{t('optional_disclaimer')}</div>
        </>
      );
      break;
    default:
      testId = 'functionality-record-detail-raw';
      content = <>{parseRecordValue(record as IRecord)}</>;
  }

  return (
    <div>
      {!hideDocs && record?.doc && <ModelDocumentation doc={record.doc} />}
      <Card className="text-base w-full" data-testid={testId}>
        {content}
      </Card>
    </div>
  );
}
